import { SelectOption } from '@globaleris/ui.library'
import { EnumCommodity } from '../../api'
import { apiPriceEngine, apiRunPriceEngine } from '../../api/Argentia/endpoints/engine/engine.api'
import { apiGetFranjasHorarias } from '../../api/Argentia/endpoints/franjaHoraria/franjaHoraria.api'
import {
  apiDeletePriceComponentType,
  apiGetDefaultPriceComponentByTypeId,
  apiGetPriceComponentByPcTypeID,
  apiPatchPriceComponentType,
  apiPostPriceComponentType,
  apiSearchPriceComponentTypeInstances,
  fetchParameterBasePriceComponents,
  PriceComponentModelV2,
  PriceComponentTypeModel
} from '../../api/Argentia/endpoints/priceComponents'
import { PricePeriodTableModel } from '../../api/Argentia/endpoints/pricePeriods/model/pricePeriods'
import {
  apiBulkDeletePricePeriodValues,
  apiGetPricePeriodByID,
  apiUpdatePricePeriodValues
} from '../../api/Argentia/endpoints/pricePeriods/pricePeriods.api'
import { CreateOrUpdatePriceValueRequest } from '../../api/Argentia/endpoints/pricing/model/prices'
import {
  PostPricePeriodValueGroupedModel,
  PriceValuesModel
} from '../../api/Argentia/endpoints/pricing/model/values'
import {
  apiDeletePrice,
  apiDeletePriceValue,
  apiGetPriceCurrentValues,
  apiPatchPrice,
  apiPostCheckCodeRepeated,
  apiPostCheckValuesDates,
  apiPricesPOST,
  apiValuePOST,
  fetchParameterPricesBaseComponents,
  fetchParameterPricesWithoutPriceComponents,
  fetchPriceByID,
  fetchPrices,
  fetchPricesNotSubscribed,
  fetchPriceValues,
  fetchSubscribedPrices
} from '../../api/Argentia/endpoints/pricing/pricing.api'
import {
  apiDeleteVersion,
  apiHandleBatchVersions,
  apiPostVersion,
  fetchVersionsByPriceId
} from '../../api/Argentia/endpoints/versions/versions.api'
import { EnumPriceCalculationType, EnumPriceComponentReference } from '../../api/Argentia/enums'
import { EditablePriceModel } from '../../api/Argentia/model/editablePriceModel'
import { EnumTipoDocumento } from '../../api/oData'
import { EnumPrecioUnitario } from '../../api/oData/enums/enumPrecioUnitario'
import { EnumPriceType } from '../../api/oData/enums/enumPriceType'
import { apiDownloadFiles } from '../../api/Pioneer/endpoints/files/files.api'
import { EnumFileType } from '../../api/Pioneer/enums/enumFileType'
import { mapFileStreamToFile } from '../../helpers/fileStreamToFile'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveCompany, setIsWaitingConfirmationDialog } from '../../redux'
import {
  mapArgentiaPricingTableModelToPricingTableModel,
  mapNewPricePOST,
  mapNewValuePOST,
  mapSingleRequestToBatch,
  mapUpdateValuePOST,
  timeFramesElementsMapperToDropdown
} from './pricing.mapper'
import {
  ModifiedVersion,
  NewPriceModel,
  NewPriceVersion,
  PriceValues,
  PricingTableModel
} from './pricing.Model'

export const fetchPricingListItems = async (
  commodity: EnumCommodity,
  priceType: EnumPriceType
): Promise<ServiceResponse<any>> => {
  return await fetchPrices(commodity, [priceType])
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPricingListItemsNotSubscribed = async (
  commodityID: EnumCommodity,
  priceType: EnumPriceType
): Promise<ServiceResponse<PricingTableModel[] | undefined>> => {
  return await fetchPricesNotSubscribed(commodityID, priceType)
    .then(res => {
      return createSuccessServiceResponse(mapArgentiaPricingTableModelToPricingTableModel(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPricingParametersListItems = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<any>> => {
  return await fetchParameterPricesWithoutPriceComponents(commodity)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// OBSOLETE -- use fetchPricingParametersBasePriceComponentsListItems() instead
export const fetchPricingParametersBaseComponentsListItems = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<any>> => {
  return await fetchParameterPricesBaseComponents(commodity)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPricingParametersBasePriceComponentsListItems = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<PricingTableModel[] | undefined>> => {
  return await fetchParameterBasePriceComponents(commodity)
    .then(res => {
      return createSuccessServiceResponse(mapArgentiaPricingTableModelToPricingTableModel(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPriceDetails = async (id: number): Promise<ServiceResponse<any>> => {
  return await fetchPriceByID(id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

/**
 * fetchPriceCurrentValues function
 * This function will retrieve an array of current priceValues for a given pricesIds and a dateperiod range.
 * If DatePeriod ranges is today, then priceValues are current.
 * @param pricesIds - array of prices IDs to retrieve priceValue information from.
 * @param startDate - startDate of rangePeriod
 * @param endDate - endDate of rangePeriod
 * @returns Array of priceValues valid within the datePeriod range. API responds with priceId property to find out
 * whom priceValues correlate with.
 */

export const fetchPriceCurrentValues = async (
  pricesIds: number[],
  startDate: string,
  endDate: string
): Promise<ServiceResponse<PriceValuesModel[] | undefined>> => {
  return await apiGetPriceCurrentValues(pricesIds, startDate, endDate)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPricingEditValuesList = async (id: number): Promise<ServiceResponse<any>> => {
  return await fetchPriceValues(id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchSubscribedPricesById = async (id: number): Promise<ServiceResponse<any>> => {
  return await fetchSubscribedPrices(id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const add = async (
  price: NewPriceModel,
  commodity: EnumCommodity,
  priceType: EnumPriceType,
  expression?: string,
  priceUnit?: EnumPrecioUnitario,
  priceCalculationType?: EnumPriceCalculationType
): Promise<ServiceResponse<any>> => {
  return await apiPricesPOST(
    mapNewPricePOST(price, getActiveCompany()?.id, expression, priceUnit, priceCalculationType)
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const addValue = async (
  priceId,
  value: PriceValues,
  priceComponentId?: number
): Promise<ServiceResponse<any>> => {
  const request: CreateOrUpdatePriceValueRequest = {
    values: mapNewValuePOST(value),
    priceComponentId: priceComponentId ?? null
  }

  return await apiValuePOST(priceId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateValue = async (
  priceId,
  value: PriceValues,
  priceComponentId?: number
): Promise<ServiceResponse<any>> => {
  const request: CreateOrUpdatePriceValueRequest = {
    values: mapUpdateValuePOST(value),
    priceComponentId: priceComponentId ?? null
  }
  return await apiValuePOST(priceId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFranjasHorariasToList = async (): Promise<SelectOption[] | null> => {
  return await fetchFranjasHorarias()
    .then(response => {
      const timeFramesList = response.data
      return timeFramesElementsMapperToDropdown(timeFramesList)
    })
    .catch(err => {
      return null
    })
}

export const fetchFranjasHorarias = async (): Promise<ServiceResponse<any>> => {
  return await apiGetFranjasHorarias()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deletePrice = async (priceId: number): Promise<ServiceResponse<any>> => {
  return await apiDeletePrice(priceId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deletePriceValue = async (
  priceId: number,
  priceValueId: number,
  priceComponentID?: number | null
): Promise<ServiceResponse<any>> => {
  return await apiDeletePriceValue(priceId, priceValueId, priceComponentID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const savePrice = async (
  priceId: number,
  price: EditablePriceModel
): Promise<ServiceResponse<any>> => {
  return await apiPatchPrice(priceId, price)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const recalculateValues = async (priceId: number): Promise<ServiceResponse<any>> => {
  // Temporary until API its finished.
  apiRunPriceEngine(priceId)
  setIsWaitingConfirmationDialog(false)
  return { success: true }

  // return await apiRunPriceEngine(priceId).then(response => {
  // return { success: true };
  // return createSuccessServiceResponse(response);
  // });
  // .catch(error => {
  //   return createErrorServiceResponse(error);
  // });
}

export const recalculatePricesValues = async (priceId: number): Promise<ServiceResponse<any>> => {
  // Temporary until API its finished.
  apiPriceEngine(priceId)
  setIsWaitingConfirmationDialog(false)
  return { success: true }

  // return await apiRunPriceEngine(priceId).then(response => {
  // return { success: true };
  // return createSuccessServiceResponse(response);
  // });
  // .catch(error => {
  //   return createErrorServiceResponse(error);
  // });
}

export const checkPriceCodeRepeated = async (
  priceCode: string,
  priceId?: number
): Promise<ServiceResponse<any>> => {
  return await apiPostCheckCodeRepeated(priceCode, priceId || 0)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkValuesDates = async (
  priceValue: PriceValues,
  priceId: number
): Promise<ServiceResponse<any>> => {
  return await apiPostCheckValuesDates(
    priceValue.startDate,
    priceValue.endDate,
    priceId,
    priceValue.id
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPriceVersions = async (priceId: number): Promise<ServiceResponse<any>> => {
  return await fetchVersionsByPriceId(priceId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteVersion = async (versionId: number): Promise<ServiceResponse<any>> => {
  return await apiDeleteVersion(versionId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveVersion = async (
  priceId: number,
  version: Partial<NewPriceVersion>
): Promise<ServiceResponse<any>> => {
  const request = {
    ...version,
    price: {
      '@odata.id': `prices(${priceId})`
    }
  }
  return await apiPostVersion(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveVersionsBatch = async (
  versions: ModifiedVersion[],
  priceId: number
): Promise<ServiceResponse<any>> => {
  const request = mapSingleRequestToBatch(versions, priceId)

  return await apiHandleBatchVersions(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createPriceComponentType = async (
  request: PriceComponentTypeModel
): Promise<ServiceResponse<PriceComponentTypeModel | undefined>> => {
  return await apiPostPriceComponentType(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updatePriceComponentType = async (
  priceComponentTypeId: number,
  request: PriceComponentTypeModel
): Promise<ServiceResponse<PriceComponentTypeModel | undefined>> => {
  return await apiPatchPriceComponentType(priceComponentTypeId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deletePriceComponentType = async (
  priceComponentTypeId: number
): Promise<ServiceResponse<null | undefined>> => {
  return await apiDeletePriceComponentType(priceComponentTypeId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPriceComponentByPcTypeID = async (
  priceComponentTypeId: number,
  commodity: EnumCommodity,
  companyId: number
): Promise<ServiceResponse<PriceComponentModelV2[] | undefined>> => {
  return await apiGetPriceComponentByPcTypeID(priceComponentTypeId, commodity, companyId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPriceComponentDefaultValue = async (
  priceComponentTypeId: number,
  referenceType: EnumPriceComponentReference,
  commodity: EnumCommodity,
  companyId: number
): Promise<ServiceResponse<PriceComponentModelV2 | undefined>> => {
  return await apiGetDefaultPriceComponentByTypeId(
    priceComponentTypeId,
    referenceType,
    commodity,
    companyId
  )
    .then(response => {
      return createSuccessServiceResponse(response || undefined)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPriceComponentInstancesBySearch = async (request: {
  priceComponentTypeId: number
  commodity: number
  companyId: number
  referenceType: number
  searchText?: string
  leadIds?: number[]
  tollIds?: number[]
  presionTensionIds?: number[]
  regionImpuestosIds?: number[]
}): Promise<ServiceResponse<PriceComponentModelV2[] | undefined>> => {
  return await apiSearchPriceComponentTypeInstances(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPricePeriodByID = async (
  priceID: number
): Promise<ServiceResponse<PricePeriodTableModel | undefined>> => {
  return await apiGetPricePeriodByID(priceID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updatePricePeriodValues = async (
  priceID: number,
  values: PostPricePeriodValueGroupedModel
): Promise<ServiceResponse<any>> => {
  return await apiUpdatePricePeriodValues(priceID, values)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const bulkDeletePricePeriodValues = async (
  priceID: number,
  priceValuesIds: number[]
): Promise<ServiceResponse<any>> => {
  return await apiBulkDeletePricePeriodValues(priceID, priceValuesIds)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadPricingTemplate = async (): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadFiles({
    blobFiles: [
      { documentType: EnumFileType.Template, tipoDocumento: EnumTipoDocumento.CargaPrecios }
    ]
  })
    .then(response => createSuccessServiceResponse(mapFileStreamToFile(response)))
    .catch(error => createErrorServiceResponse(error))
}
